import { LogOut, Trash2, Sun, Moon, ChevronRight, Shield, MessageSquare, Globe, Zap, Clock, Key } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePreferences } from '../../contexts/PreferencesContext';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { NavigationBar } from '../../components/ui/NavigationBar';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { AlertModal } from '../../components/ui/AlertModal';
import { theme, styles } from '../../lib/theme';
import { useState, useEffect, useRef } from 'react';
import { LanguagePickerModal } from '../../components/ui/LanguagePickerModal';
import { useTranslations } from '../../i18n';
import { getLanguageConfig } from '../../lib/languages';
import { SupportedLanguage } from '../../lib/expenseTypeHelpers';
import planLimitsData from '../../data/planLimits.json';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';
import { getCarLimitStatus } from '../../lib/carLimits';
import { dataManager } from '../../lib/dataManager';

type SettingsProps = {
  onBack: () => void;
};

export function Settings({ onBack }: SettingsProps) {
  const navigate = useNavigate();
  const { preferences, updatePreferences } = usePreferences();
  const { t } = useTranslations();
  const { user, signOut, isAdmin } = useAuth();

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{ isOpen: boolean; title: string; message: string }>({ isOpen: false, title: '', message: '' });
  const [usageStats, setUsageStats] = useState({ emails: 0, files: 0 });
  const [cars, setCars] = useState<any[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Restore scroll position
  useScrollRestoration(scrollContainerRef);

  useEffect(() => {
    const fetchUsage = async () => {
      if (!user) return;
      try {
        const { data } = await supabase.rpc('get_user_parsing_usage', {
          p_user_id: user.id,
          p_is_pro: preferences?.plan_type === 'pro'
        });
        if (data && data.length > 0) {
          setUsageStats({
            emails: parseInt(data[0].email_count) || 0,
            files: parseInt(data[0].file_count) || 0
          });
        }
      } catch (err) {
        console.error('Error fetching limits:', err);
      }
    };
    if (preferences) {
      fetchUsage();
    }
  }, [user?.id, preferences?.plan_type]);

  // Fetch cars for limit status
  useEffect(() => {
    const fetchCars = async () => {
      if (!user) return;
      try {
        const fetchedCars = await dataManager.fetchCars(user.id);
        setCars(fetchedCars);
      } catch (err) {
        console.error('Error fetching car count:', err);
      }
    };
    if (user) fetchCars();
  }, [user?.id]);

  const [subDetails, setSubDetails] = useState<{
    active: boolean;
    status: string;
    current_period_end: number;
    price: number;
    currency: string;
    cancel_at_period_end: boolean;
  } | null>(null);

  const formatPeriodEnd = (ts: any) => {
    if (!ts) return 'N/A';
    const ms = ts > 10000000000 ? ts : ts * 1000;
    const date = new Date(ms);
    return isNaN(date.getTime()) ? `N/A (${ts})` : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
  };

  useEffect(() => {
    if (preferences?.plan_type === 'pro') {
      const fetchSubDetails = async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) return;

          const workerUrl = import.meta.env.VITE_WORKER_URL || 'https://mycarcosts.gtreulands.workers.dev';
          const res = await fetch(`${workerUrl}/stripe/subscription-details`, {
            headers: {
              'Authorization': `Bearer ${session.access_token}`
            }
          });
          if (res.ok) {
            const data = await res.json();
            if (data.active) {
              setSubDetails(data);
            }
          }
        } catch (err) {
          console.error('Failed to fetch subscription details:', err);
        }
      };
      fetchSubDetails();
    }
  }, [preferences?.plan_type]);


  const handleLogout = async () => {
    setShowLogoutModal(false);
    await signOut();
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    setIsDeleting(true);
    setShowDeleteModal(false);

    try {
      // First delete associated cars (cascading will handle expenses/images usually, 
      // but let's be explicit if needed or rely on DB)
      await supabase.from('cars').delete().eq('user_id', user.id);

      // Delete the user via admin API (requires proper permissions on Supabase)
      const { error } = await supabase.auth.admin.deleteUser(user.id);
      if (error) throw error;

      await signOut();
    } catch (error) {
      console.error('Error deleting account:', error);
      setAlertConfig({ isOpen: true, title: t.common.error, message: 'Failed to delete account. Note: This action might require recent login or administrative privileges.' });
    } finally {
      setIsDeleting(false);
    }
  };

  if (!preferences) return null;

  const planType = preferences.plan_type || 'free';
  const planConfig = (planLimitsData as any)[planType] || (planLimitsData as any)['free'];

  const emailParsingLimit = (preferences.email_parsing_limit ?? 0) > 0 ? preferences.email_parsing_limit : planConfig.email_parsing_limit;
  const fileParsingLimit = (preferences.file_parsing_limit ?? 0) > 0 ? preferences.file_parsing_limit : planConfig.file_parsing_limit;
  const storageLimitMb = planConfig.storage_size_limit || 100;

  const usedStorageBytes = preferences.used_storage_bytes || 0;
  const usedStorageMb = (usedStorageBytes / (1024 * 1024)).toFixed(1);

  // Car limit status
  const carLimitStatus = getCarLimitStatus(
    cars,
    preferences
  );
  const carPct = Math.min(100, carLimitStatus.limit > 0 ? (carLimitStatus.current / carLimitStatus.limit) * 100 : 0);
  const carBarColor = carLimitStatus.isReached
    ? 'bg-red-500'
    : carPct >= 66
      ? 'bg-amber-500'
      : 'bg-blue-600 dark:bg-blue-500';


  const formatTimezoneWithOffset = (tz: string) => {
    try {
      const offset = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        timeZoneName: 'shortOffset'
      }).formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value || '';
      return `${tz} (${offset})`;
    } catch (e) {
      return tz;
    }
  };

  return (
    <div className="fixed inset-0 flex flex-col h-full bg-transparent overflow-hidden">
      <div className="flex-shrink-0 z-30">
        <NavigationBar
          title={t.settings.title}
          onBack={onBack}
          fixed={false}
        />
      </div>

      <div
        ref={scrollContainerRef}
        className={`flex-1 overflow-y-auto no-scrollbar pt-4 pb-32`}
      >
        <div className="max-w-2xl mx-auto px-4 space-y-8">
          {/* Main Actions - Navigation Label Removed */}
          {isAdmin && (
            <section className={theme.spacing.sectionGap}>
              <div className={`${styles.card} divide-y ${theme.colors.divider}`}>
                <button
                  onClick={() => navigate('/admin')}
                  className={`${styles.listItem} group`}
                >
                  <div className={`flex items-center ${theme.spacing.itemGap}`}>
                    <div className={`${theme.spacing.iconContainer} bg-blue-600 text-white ${theme.radius.button} flex items-center justify-center shadow-md shadow-blue-600/30 transition-transform`}>
                      <Shield size={24} />
                    </div>
                    <div className="text-left">
                      <h3 className={theme.typography.itemTitle}>{t.settings.adminConsole}</h3>
                      <p className="text-[11px] font-bold uppercase tracking-widest text-blue-600">{t.settings.systemControl}</p>
                    </div>
                  </div>
                  <ChevronRight size={20} className="text-gray-300 transition-colors group-hover:text-blue-500" />
                </button>
              </div>
            </section>
          )}

          {/* Units & Preferences */}
          <section className={theme.spacing.sectionGap}>
            <div className="px-1 py-1">
              <h3 className={theme.typography.sectionTitle}>{t.settings.systemPreferences}</h3>
            </div>
            <div className={`${styles.card} divide-y ${theme.colors.divider}`}>
              {/* Language Selection */}
              <button
                onClick={() => setShowLanguageModal(true)}
                className={`${styles.listItem} group text-left`}
              >
                <div className={`flex items-center ${theme.spacing.itemGap}`}>
                  <div className={`${theme.spacing.iconContainer} bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 ${theme.radius.button} flex items-center justify-center transition-colors`}>
                    <Globe size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className={theme.typography.itemTitle}>{t.settings.language}</h3>
                    <p className={theme.typography.itemSubtitle}>
                      {getLanguageConfig(preferences.language || 'en').flag} {getLanguageConfig(preferences.language || 'en').nativeName}
                    </p>
                  </div>
                </div>
                <ChevronRight size={20} className="text-gray-300 transition-transform group-hover:translate-x-0.5" />
              </button>

              {/* Theme Selection */}
              <div
                className={`${styles.listItem} group cursor-pointer select-none`}
                onClick={() => updatePreferences({ theme: preferences.theme === 'light' ? 'dark' : 'light' })}
              >
                <div className={`flex items-center ${theme.spacing.itemGap}`}>
                  <div className={`${theme.spacing.iconContainer} bg-amber-50 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 ${theme.radius.button} flex items-center justify-center transition-colors`}>
                    {preferences.theme === 'light' ? <Sun size={24} /> : <Moon size={24} />}
                  </div>
                  <div className="flex-1">
                    <h3 className={theme.typography.itemTitle}>{t.settings.interfaceTheme}</h3>
                    <p className={theme.typography.itemSubtitle}>
                      {preferences.theme === 'light' ? t.settings.light : t.settings.dark}
                    </p>
                  </div>
                </div>

                {/* iOS Style Switch */}
                <div
                  className={`w-14 h-8 rounded-full relative transition-all duration-300 ease-in-out ${preferences.theme === 'light'
                    ? 'bg-blue-600 shadow-inner'
                    : 'bg-gray-700 dark:bg-gray-800'
                    }`}
                >
                  <div
                    className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow-lg flex items-center justify-center transition-all duration-300 ease-in-out transform ${preferences.theme === 'light' ? 'translate-x-6' : 'translate-x-0'
                      }`}
                  >
                    {preferences.theme === 'light'
                      ? <Sun size={14} className="text-amber-500" />
                      : <Moon size={14} className="text-blue-500" />
                    }
                  </div>
                </div>
              </div>

              {/* Timezone Selection */}
              <div className="relative">
                <select
                  value={preferences.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                  onChange={(e) => updatePreferences({ timezone: e.target.value })}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                >
                  {((Intl as any).supportedValuesOf ? (Intl as any).supportedValuesOf('timeZone') : [preferences.timezone || 'Europe/Riga']).map((tz: string) => (
                    <option key={tz} value={tz}>{formatTimezoneWithOffset(tz)}</option>
                  ))}
                </select>
                <div className={`${styles.listItem} group`}>
                  <div className={`flex items-center ${theme.spacing.itemGap}`}>
                    <div className={`${theme.spacing.iconContainer} bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 ${theme.radius.button} flex items-center justify-center transition-colors`}>
                      <Globe size={24} />
                    </div>
                    <div className="flex-1">
                      <h3 className={theme.typography.itemTitle}>{t.settings.timezone || 'Timezone'}</h3>
                      <p className={theme.typography.itemSubtitle}>
                        {formatTimezoneWithOffset(preferences.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone)}
                      </p>
                      <p className="text-[11px] text-gray-400 dark:text-gray-600 mt-0.5">
                        {t.settings.timezoneDesc || 'Used for email reminders'}
                      </p>
                    </div>
                  </div>
                  <ChevronRight size={20} className="text-gray-300 transition-transform group-hover:translate-x-0.5" />
                </div>
              </div>

              {/* Time Format Selection */}
              <div
                className={`${styles.listItem} group cursor-pointer select-none`}
                onClick={() => updatePreferences({ time_format: preferences.time_format === '24h' ? '12h' : '24h' })}
              >
                <div className={`flex items-center ${theme.spacing.itemGap}`}>
                  <div className={`${theme.spacing.iconContainer} bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 ${theme.radius.button} flex items-center justify-center transition-colors`}>
                    <Clock size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className={theme.typography.itemTitle}>{t.settings.timeFormat || 'Time Format'}</h3>
                    <p className={theme.typography.itemSubtitle}>
                      {preferences.time_format === '24h' ? (t.settings.timeFormat24h || '24-hour') : (t.settings.timeFormat12h || '12-hour')}
                    </p>
                  </div>
                </div>

                {/* iOS Style Switch */}
                <div
                  className={`w-14 h-8 rounded-full relative transition-all duration-300 ease-in-out ${preferences.time_format === '24h'
                    ? 'bg-blue-600 shadow-inner'
                    : 'bg-gray-700 dark:bg-gray-800'
                    }`}
                >
                  <div
                    className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow-lg flex items-center justify-center transition-all duration-300 ease-in-out transform ${preferences.time_format === '24h' ? 'translate-x-0' : 'translate-x-6'
                      }`}
                  >
                    <div className="text-[10px] font-bold text-blue-600">
                      {preferences.time_format === '24h' ? '24' : '12'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Account Plan */}
          <section className={theme.spacing.sectionGap}>
            <div className="px-1 py-1">
              <h3 className={theme.typography.sectionTitle}>{t.settings.accountPlan || 'Account Plan'}</h3>
            </div>
            <div className={`${styles.card} divide-y ${theme.colors.divider}`}>
              <button
                onClick={() => navigate('/settings/plans')}
                className={`${styles.listItem} group`}
              >
                <div className={`flex items-center ${theme.spacing.itemGap}`}>
                  <div className={`${theme.spacing.iconContainer} ${preferences.plan_type === 'pro' ? 'bg-gradient-to-tr from-green-500 to-emerald-400' : 'bg-blue-600'} text-white ${theme.radius.button} flex items-center justify-center shadow-md transition-transform`}>
                    <Zap size={24} />
                  </div>
                  <div className="text-left">
                    <h3 className={theme.typography.itemTitle}>
                      {preferences.plan_type === 'pro' ? (t.common.planProTier || 'Pro plan') : (t.common.planFreeTier || 'Free plan')}
                    </h3>
                    {preferences?.plan_type === 'pro' && subDetails && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {subDetails.cancel_at_period_end ? (
                          <span className="text-red-500 font-medium">
                            {t.settings.endsOn || 'Ends on'}: {formatPeriodEnd(subDetails.current_period_end)}
                          </span>
                        ) : (
                          <span className="font-medium">
                            {t.settings.nextPayment || 'Next payment'}: {formatPeriodEnd(subDetails.current_period_end)} ({new Intl.NumberFormat(undefined, { style: 'currency', currency: subDetails.currency.toUpperCase() }).format(subDetails.price)})
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <ChevronRight size={20} className="text-gray-300 transition-colors group-hover:text-blue-500" />
              </button>

              <div className="p-4 space-y-4 cursor-default">
                {/* Email Limit */}
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      {t.settings.emailScans || 'Email Scans'}
                    </span>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      {usageStats.emails} / {emailParsingLimit}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${usageStats.emails >= emailParsingLimit ? 'bg-red-500' : 'bg-blue-600 dark:bg-blue-500'}`}
                      style={{ width: `${Math.min(100, (usageStats.emails / emailParsingLimit) * 100)}%` }}
                    ></div>
                  </div>
                </div>

                {/* File Limit */}
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      {t.settings.fileScans || 'Receipt Scans'}
                    </span>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      {usageStats.files} / {fileParsingLimit}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${usageStats.files >= fileParsingLimit ? 'bg-red-500' : 'bg-blue-600 dark:bg-blue-500'}`}
                      style={{ width: `${Math.min(100, (usageStats.files / fileParsingLimit) * 100)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Cars Limit */}
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      {t.settings.carsUsage || 'Cars'}
                    </span>
                    <span className={`font-semibold ${carLimitStatus.isReached ? 'text-red-500' : 'text-gray-700 dark:text-gray-300'}`}>
                      {carLimitStatus.current} / {carLimitStatus.limit}
                      {carLimitStatus.isReached && <span className="ml-1">· {t.settings.carsLimitReached || 'Limit reached'}</span>}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${carBarColor}`}
                      style={{ width: `${carPct}%` }}
                    ></div>
                  </div>
                </div>

                {/* Storage Limit */}
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      {t.settings.storageSize || 'Storage Size'}
                    </span>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      {usedStorageMb} MB / {storageLimitMb} MB
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${parseFloat(usedStorageMb.toString()) >= storageLimitMb ? 'bg-red-500' : 'bg-blue-600 dark:bg-blue-500'}`}
                      style={{ width: `${Math.min(100, (parseFloat(usedStorageMb.toString()) / storageLimitMb) * 100)}%` }}
                    ></div>
                  </div>
                </div>

              </div>

            </div>
          </section>

          {/* Account Settings */}
          <section className={theme.spacing.sectionGap}>
            <div className="px-1 py-1">
              <h3 className={theme.typography.sectionTitle}>{t.settings.accountManagement}</h3>
            </div>
            <div className={`${styles.card} divide-y ${theme.colors.divider}`}>
              <button
                onClick={() => navigate('/contact')}
                className={`${styles.listItem} group text-left`}
              >
                <div className={`flex items-center ${theme.spacing.itemGap}`}>
                  <div className={`${theme.spacing.iconContainer} bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 ${theme.radius.button} flex items-center justify-center transition-colors`}>
                    <MessageSquare size={24} />
                  </div>
                  <div>
                    <h3 className={theme.typography.itemTitle}>{t.settings.contactUs}</h3>
                    <p className={theme.typography.itemSubtitle}>{t.settings.contactDesc}</p>
                  </div>
                </div>
                <ChevronRight size={20} className="text-gray-300 transition-transform group-hover:translate-x-0.5" />
              </button>

              <button
                onClick={() => setShowLogoutModal(true)}
                className={`${styles.listItem} hover:bg-red-50/50 group text-left`}
              >
                <div className={`flex items-center ${theme.spacing.itemGap}`}>
                  <div className={`${theme.spacing.iconContainer} ${theme.colors.errorLight} ${theme.colors.errorText} ${theme.radius.button} flex items-center justify-center transition-colors`}>
                    <LogOut size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className={`${theme.typography.itemTitle} text-left`}>{t.settings.logOut}</h3>
                    <p className={theme.typography.itemSubtitle}>{user?.email}</p>
                  </div>
                </div>
                <ChevronRight size={20} className="text-gray-300 transition-transform group-hover:translate-x-0.5" />
              </button>

              <button
                onClick={() => setShowDeleteModal(true)}
                className={`${styles.listItem} hover:bg-red-500/5 dark:hover:bg-red-500/10 group text-left`}
              >
                <div className={`flex items-center ${theme.spacing.itemGap}`}>
                  <div className={`${theme.spacing.iconContainer} ${theme.colors.errorLight} ${theme.colors.errorText} ${theme.radius.button} flex items-center justify-center`}>
                    <Trash2 size={24} />
                  </div>
                  <h3 className={`${theme.typography.itemTitle} ${theme.colors.errorText} text-left`}>{t.settings.deleteAccount}</h3>
                </div>
                <ChevronRight size={20} className={`${theme.colors.errorText} opacity-30 transition-transform group-hover:translate-x-0.5`} />
              </button>
            </div>
          </section>


        </div>
      </div >

      <ConfirmModal
        isOpen={showLogoutModal}
        title={t.settings.logOut}
        message={t.settings.logOutConfirmMessage}
        confirmLabel={t.settings.logOut}
        cancelLabel={t.common.cancel}
        onConfirm={handleLogout}
        onCancel={() => setShowLogoutModal(false)}
        onClose={() => setShowLogoutModal(false)}
        isDestructive={true}
      />

      <ConfirmModal
        isOpen={showDeleteModal}
        title={t.settings.deleteAccount}
        message={t.settings.deleteAccountConfirmMessage}
        confirmLabel={isDeleting ? t.settings.deleting : t.settings.deletePermanently}
        cancelLabel={t.settings.keepAccount}
        onConfirm={handleDeleteAccount}
        onCancel={() => setShowDeleteModal(false)}
        onClose={() => setShowDeleteModal(false)}
        isDestructive={true}
      />

      <LanguagePickerModal
        isOpen={showLanguageModal}
        title={t.settings.language}
        currentLanguage={preferences.language || 'en'}
        onClose={() => setShowLanguageModal(false)}
        onSelect={(lang: SupportedLanguage) => {
          updatePreferences({ language: lang });
          setShowLanguageModal(false);
        }}
      />
      <AlertModal
        isOpen={alertConfig.isOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        confirmLabel={t.common.ok}
        onClose={() => setAlertConfig({ isOpen: false, title: '', message: '' })}
      />
    </div >
  );
}
